import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { InputType } from 'config'
import type { IClosingCostSetup } from 'pages/Admin/AdminTools/Configuration/HUD1/ClosingCostSetup/type'
import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'stories/components'
import { InputValidate, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { validateFeeAmount } from './closingCalendarFeeValidation'
import { IFeeData } from './types'

export interface FeeDialogProps {
  modalTitle: string
  onClose: () => void
  onSubmit: Function
  data: IFeeData[]
  closingCostSetups?: IClosingCostSetup[]
  proposedLoanAmount?: number
}

type FeeRow = { lineId?: number; group: Record<string, InputType> }

const feeRowFromItem = (item?: IFeeData): FeeRow => ({
  lineId: item?.lineId,
  group: {
    title: { title: 'Title', inputType: 'text', prefix: '', value: item?.title, error: '', required: true },
    amount: {
      title: 'Amount',
      inputType: 'text',
      type: 'thousandSep',
      prefix: '$',
      value: item?.amount,
      error: '',
      required: true,
    },
  },
})

export const FeeDialog = (props: FeeDialogProps) => {
  const { data, modalTitle, closingCostSetups = [], proposedLoanAmount = 0 } = props
  const [rows, setRows] = useState<FeeRow[]>([])
  const [loading, setLoading] = useState(false)

  const setupByLineId = useMemo(() => new Map(closingCostSetups.map((s) => [s.lineId, s])), [closingCostSetups])

  const onClose = () => {
    props.onClose()
  }

  useEffect(() => {
    if (data.length > 0) {
      setRows(data.map((item) => feeRowFromItem(item)))
    } else {
      setRows([feeRowFromItem()])
    }
  }, [data])

  const handleAddInputs = () => {
    setRows([...rows, feeRowFromItem()])
  }

  const onChange = (rowIndex: number, key: string, value: string) => {
    const next = cloneDeep(rows)
    next[rowIndex].group[key].value = value
    setRows(next)
  }

  const handleRemoveGroup = (rowIndex: number) => {
    setRows(rows.filter((_, i) => i !== rowIndex))
  }

  const onSubmit = async () => {
    const next = cloneDeep(rows)
    let hasError = false
    next.forEach((row, rowIndex) => {
      Object.keys(row.group).forEach((key) => {
        next[rowIndex].group[key].error = InputValidate(row.group[key])
        if (next[rowIndex].group[key].error) hasError = true
      })
    })

    const loanAmt = Number(proposedLoanAmount) || 0
    next.forEach((row, rowIndex) => {
      const amtRaw = removeComma(String(row.group.amount.value ?? ''))
      const amt = Number(amtRaw)
      if (!Number.isFinite(amt)) return
      const setup = row.lineId != null ? setupByLineId.get(row.lineId) : undefined
      const boundErr = validateFeeAmount(amt, setup, loanAmt)
      if (boundErr) {
        next[rowIndex].group.amount.error = boundErr
        hasError = true
      }
    })

    setRows(next)
    if (hasError) return

    setLoading(true)
    const feeData: IFeeData[] = rows.map((row) => ({
      lineId: row.lineId,
      title: String(row.group.title.value ?? ''),
      amount: removeComma(row.group.amount.value),
    }))

    props.onSubmit(feeData)
    setLoading(false)
    onClose()
  }

  return (
    <Modal title={modalTitle} isOpen loading={loading} lastUpdatedAt={Date.now()} onOk={onSubmit} onClose={onClose}>
      <div className="w-200">
        {rows?.map((row, rowIndex) => (
          <div key={rowIndex} className="flex gap-4 mb-4 items-start">
            {Object.keys(row.group).map((key, index) => (
              <div key={key} className={`${index === 0 ? '' : 'flex-1'}`}>
                <RenderInput
                  input={row.group[key]}
                  Key={`${rowIndex}-${key}`}
                  onChange={(_: any, value: string) => onChange(rowIndex, key, value)}
                />
              </div>
            ))}
            <button type="button" onClick={() => handleRemoveGroup(rowIndex)} className="text-red-600">
              <TrashIcon className="w-4 h-4"></TrashIcon>
            </button>
          </div>
        ))}
        <div>
          <button
            className="font-variation-settings-600 text-sm font-medium text-shade-blue hover:underline whitespace-nowrap"
            onClick={handleAddInputs}
          >
            + Add
          </button>
        </div>
      </div>
    </Modal>
  )
}
